// store/companies/thunks.ts

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetCompaniesParams,
  GetCompanyRequest,
  CompanyCreateSchema,
  CompanyUpdateSchema,
  ContactCreateSchema,
  ContactUpdateSchema,
  CompanySchema,
} from '@/types/companies';
import { companiesService } from '../../services/companies.service';
import { showSnackbar } from '../snakebar/slice';

// --------------------------------------- CORE COMPANIES ---------------------------------------

export const getCompanies = createAsyncThunk(
  'companies/getCompanies',
  async (params: GetCompaniesParams | undefined, { rejectWithValue }) => {
    try {
      const response = await companiesService.getCompanies(params);
      // Return both data and total for pagination
      return {
        data: response.data,
        total: response.total,
        offset: params?.offset || 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch companies');
    }
  }
);

export const getCompany = createAsyncThunk(
  'companies/getCompany',
  async (request: GetCompanyRequest, { rejectWithValue }) => {
    try {
      const response = await companiesService.getCompany(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch company');
    }
  }
);

export const createCompany = createAsyncThunk(
  'companies/createCompany',
  async (companyData: CompanyCreateSchema, { dispatch, rejectWithValue }) => {
    try {
      const response = await companiesService.createCompany(companyData);
      dispatch(
        showSnackbar({
          message: 'Successfully created company!',
          variant: 'success',
        })
      );
      return response;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error instanceof Error ? error.message : 'Failed to create company',
          variant: 'error',
        })
      );
      return rejectWithValue(error.response?.data?.message || 'Failed to create company');
    }
  }
);

export const updateCompany = createAsyncThunk(
  'companies/updateCompany',
  async (params: { companyId: string; companyData: CompanyUpdateSchema }, { rejectWithValue }) => {
    try {
      const response = await companiesService.updateCompany(params.companyId, params.companyData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update company');
    }
  }
);

export const deleteCompany = createAsyncThunk(
  'companies/deleteCompany',
  async (companyId: string, { rejectWithValue }) => {
    try {
      await companiesService.deleteCompany(companyId);
      return { companyId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete company');
    }
  }
);

// ---------------------------------- COMPANY CONTACTS MANAGEMENT ----------------------------------

export const getCompanyContacts = createAsyncThunk(
  'companies/getCompanyContacts',
  async (companyId: string, { rejectWithValue }) => {
    try {
      const response = await companiesService.getCompanyContacts(companyId);
      console.log('Thunk: Fetched company contacts:', response);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch company contacts');
    }
  }
);

export const createCompanyContact = createAsyncThunk(
  'companies/createCompanyContact',
  async (params: { companyId: string; contactData: ContactCreateSchema }, { rejectWithValue }) => {
    try {
      const response = await companiesService.createCompanyContact(
        params.companyId,
        params.contactData
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create company contact');
    }
  }
);

export const updateCompanyContact = createAsyncThunk(
  'companies/updateCompanyContact',
  async (
    params: { companyId: string; contactId: string; contactData: ContactUpdateSchema },
    { rejectWithValue }
  ) => {
    try {
      const response = await companiesService.updateCompanyContact(
        params.companyId,
        params.contactId,
        params.contactData
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update company contact');
    }
  }
);

export const deleteCompanyContact = createAsyncThunk(
  'companies/deleteCompanyContact',
  async (params: { companyId: string; contactId: string }, { rejectWithValue }) => {
    try {
      await companiesService.deleteCompanyContact(params.companyId, params.contactId);
      return { companyId: params.companyId, contactId: params.contactId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete company contact');
    }
  }
);

export const getCompanyStudies = createAsyncThunk(
  'companies/getCompanyStudies',
  async (companyId: string, { rejectWithValue }) => {
    try {
      const response = await companiesService.getCompanyStudies(companyId);
      console.log(`Fetched ${response.length} studies for company ${companyId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch company studies');
    }
  }
);

// ------------------------------------ GROUPED EXPORTS ------------------------------------

export const companies = {
  // Core Companies
  getCompanies,
  getCompany,
  createCompany,
  updateCompany,
  deleteCompany,

  // Company Contacts
  getCompanyContacts,
  createCompanyContact,
  updateCompanyContact,
  deleteCompanyContact,

  // Company Studies
  getCompanyStudies,
};
